import { useCallback, useEffect, useState, type CSSProperties, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { Cross2Icon } from '@radix-ui/react-icons';
import { toast } from 'sonner';
import { FormSection } from '@/shared/ui/components/form';
import { Button, CardListSkeleton, Image, Text } from '@/shared/ui';
import { PropertiesService } from '@/services/requests/housien-services/PropertiesService';
import { SortableImageItem } from './SortableImageItem';
import { MAX_IMAGE_UPLOAD_BYTES } from '@/shared/utils/images-manipulation';

interface PhotoUploadSectionProps {
  pendingImagesRef: MutableRefObject<File[]>;
  agencyId?: string;
  propertyId?: string;
  fallbackImageKeys?: string[];
  onImageOrderChange?: (urls: string[]) => void;
}

const MAX_IMAGES = 30;
const MAX_FILE_SIZE = MAX_IMAGE_UPLOAD_BYTES;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const dropzoneStyle: CSSProperties = {
  border: '2px dashed var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: '24px 16px',
  textAlign: 'center',
  cursor: 'pointer',
  background: 'var(--surface-tertiary)',
  transition: 'border-color 0.15s ease',
};

/**
 * Renders the property photo upload section: a drag-and-drop dropzone, a
 * sortable grid of existing images, and previews of pending uploads. Existing
 * images are fetched on mount and reordered via drag-and-drop; pending files
 * are buffered in `pendingImagesRef` until the form is saved.
 */
export function PhotoUploadSection({ pendingImagesRef, agencyId, propertyId, fallbackImageKeys, onImageOrderChange }: PhotoUploadSectionProps) {
  const { t } = useTranslation('features/properties');
  const [previews, setPreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!agencyId || !propertyId) return;
    setLoadingExisting(true);
    PropertiesService.getPropertyImageUrls(agencyId, propertyId)
      .then(res => {
        const urls = res.urls ?? [];
        if (urls.length > 0) {
          setExistingImages(urls);
        } else if (fallbackImageKeys && fallbackImageKeys.length > 0) {
          setExistingImages(fallbackImageKeys);
        }
      })
      .catch(() => {
        if (fallbackImageKeys && fallbackImageKeys.length > 0) {
          setExistingImages(fallbackImageKeys);
        }
      })
      .finally(() => setLoadingExisting(false));
  }, [agencyId, propertyId]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const currentTotal = pendingImagesRef.current.length + existingImages.length;
    const valid = Array.from(files).filter(
      f => ACCEPTED_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE,
    );
    const toAdd = valid.slice(0, MAX_IMAGES - currentTotal);
    pendingImagesRef.current = [...pendingImagesRef.current, ...toAdd];
    setPreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))]);
  }, [pendingImagesRef, existingImages.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const removePendingImage = useCallback((index: number) => {
    pendingImagesRef.current = pendingImagesRef.current.filter((_, i) => i !== index);
    setPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  }, [pendingImagesRef]);

  const removeExistingImage = useCallback((url: string) => {
    if (!agencyId || !propertyId || !url) return;
    setExistingImages(prev => prev.filter(u => u !== url));
    PropertiesService.deletePropertyImage(agencyId, propertyId, url).catch(() => {
      setExistingImages(prev => [...prev, url]);
      toast.error(t('propertyDrawer.photos.deleteFailed'));
    });
  }, [agencyId, propertyId, t]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setExistingImages(prev => {
      const oldIdx = prev.indexOf(active.id as string);
      const newIdx = prev.indexOf(over.id as string);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      onImageOrderChange?.(reordered);
      return reordered;
    });
  }, [onImageOrderChange]);

  const openFilePicker = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_TYPES.join(',');
    input.onchange = () => { if (input.files) handleFiles(input.files); };
    input.click();
  }, [handleFiles]);

  const hasAnyImage = existingImages.length > 0 || previews.length > 0;

  return (
    <FormSection title={t('propertyDrawer.photos.title')}>
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={openFilePicker}
        style={dropzoneStyle}
        role="button"
        aria-label={t('propertyDrawer.photos.uploadAriaLabel')}
      >
        <Text style={{ fontSize: 13, fontFamily: 'var(--font-family)', color: 'var(--text-secondary)', margin: 0 }}>
          {t('propertyDrawer.photos.dropzonePrompt')}
        </Text>
        <Text style={{ fontSize: 11, fontFamily: 'var(--font-family)', color: 'var(--text-tertiary)', margin: 'var(--space-1) 0 0' }}>
          {t('propertyDrawer.photos.dropzoneHint', { max: MAX_IMAGES })}
        </Text>
      </div>

      {loadingExisting && <CardListSkeleton count={2} />}

      {!loadingExisting && hasAnyImage && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={existingImages} strategy={rectSortingStrategy}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
              {existingImages.map((src, i) => (
                <SortableImageItem
                  key={src}
                  id={src}
                  src={src}
                  alt={`Image ${i + 1}`}
                  onRemove={() => removeExistingImage(src)}
                  isFirst={i === 0}
                />
              ))}
              {previews.map((src, i) => (
                <div key={`pending-${i}`} className="relative rounded-(--radius-md) overflow-hidden aspect-square">
                  <Image src={src} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                  <Button
                    variant="none"
                    disableSize
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removePendingImage(i); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[var(--overlay-scrim)] flex items-center justify-center"
                  >
                    <Cross2Icon className="w-3 h-3 text-[var(--text-inverse)]" />
                  </Button>
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </FormSection>
  );
}
